import { useState, useMemo } from 'react'
import { motion } from 'framer-motion'
import type { ChatMessage as ChatMessageType, Agent } from '../../stores/chatStore'
import { useChatStore } from '../../stores/chatStore'
import AgentCard from './AgentCard'

interface ChatMessageProps {
  message: ChatMessageType
  isLatest?: boolean
}

export default function ChatMessage({ message, isLatest = false }: ChatMessageProps) {
  const isUser = message.role === 'user'

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-full ${isUser ? 'max-w-md' : 'w-full'}`}>
        {isUser ? (
          <UserMessage content={message.content} />
        ) : (
          <AssistantMessage
            content={message.content}
            agents={message.agents}
            total={message.total}
            isLatest={isLatest}
          />
        )}
      </div>
    </div>
  )
}

function UserMessage({ content }: { content: string }) {
  return (
    <div className="inline-block px-4 py-2 rounded-lg bg-[var(--accent-teal)] text-[var(--bg-primary)]">
      <p className="font-mono text-sm">{content}</p>
    </div>
  )
}

type FilterKey = 'callable' | 'free' | 'paid' | 'a2a' | 'mcp' | 'reputation' | 'verified'

const FILTER_CHIPS: { key: FilterKey; label: string; emoji: string }[] = [
  { key: 'callable', label: 'Callable', emoji: '✅' },
  { key: 'free', label: 'Free', emoji: '🆓' },
  { key: 'paid', label: 'x402', emoji: '💰' },
  { key: 'a2a', label: 'A2A', emoji: '🤝' },
  { key: 'mcp', label: 'MCP', emoji: '🔧' },
  { key: 'reputation', label: 'Has Reputation', emoji: '⭐' },
  { key: 'verified', label: 'Domain Verified', emoji: '🔒' },
]

function matchesFilter(agent: Agent, filter: FilterKey): boolean {
  switch (filter) {
    case 'callable': return agent.availability === 'callable'
    case 'free': return agent.x402_support !== 'true'
    case 'paid': return agent.x402_support === 'true' ||
      (agent.metadata?.x402Support === true) ||
      ((agent.metadata?.additional_fields as Record<string, unknown>)?.x402support === true) ||
      ((agent.metadata?.additional_fields as Record<string, unknown>)?.x402Support === true)
    case 'a2a': return agent.endpoints.some(e => e.protocol_type === 'a2a')
    case 'mcp': return agent.endpoints.some(e => e.protocol_type === 'mcp')
    case 'reputation': return (agent.reputation_count ?? 0) > 0
    case 'verified': return agent.domain_verified === true
  }
}

const PAGE_SIZE = 10

function AssistantMessage({
  content,
  agents,
  total,
  isLatest,
}: {
  content: string
  agents?: ChatMessageType['agents']
  total?: number
  isLatest?: boolean
}) {
  const { searchResults, searchTotal, lastSearchParams, lastQuery, isLoadingMore, setLoading, setSearchResults: setSearchResultsStore, appendSearchResults, setLoadingMore } = useChatStore()
  const [activeFilters, setActiveFilters] = useState<Set<FilterKey>>(new Set())
  const [currentPage, setCurrentPage] = useState(1)

  // Use store state for latest message (allows "load more" to append), otherwise use message props
  const displayedAgents = isLatest && searchResults.length > 0 ? searchResults : agents
  const displayedTotal = isLatest && searchTotal > 0 ? searchTotal : total
  const hasMore = isLatest && displayedAgents && displayedTotal && displayedAgents.length < displayedTotal

  const filteredAgents = useMemo(() => {
    if (!displayedAgents || activeFilters.size === 0) return displayedAgents
    return displayedAgents.filter(agent =>
      [...activeFilters].every(f => matchesFilter(agent, f))
    )
  }, [displayedAgents, activeFilters])

  // Pagination
  const totalFiltered = filteredAgents?.length ?? 0
  const totalPages = Math.ceil(totalFiltered / PAGE_SIZE)
  const paginatedAgents = useMemo(() => {
    if (!filteredAgents) return filteredAgents
    const start = (currentPage - 1) * PAGE_SIZE
    return filteredAgents.slice(start, start + PAGE_SIZE)
  }, [filteredAgents, currentPage])

  const displayContent = useMemo(() => {
    if (!displayedAgents || !filteredAgents) return content

    // When no filters are active
    if (activeFilters.size === 0) {
      // If data was re-fetched (store differs from message props), show updated summary
      if (displayedAgents !== agents) {
        const totalCount = displayedTotal ?? displayedAgents.length
        const callableCount = displayedAgents.filter(a => a.availability === 'callable').length
        const protocols = new Set(displayedAgents.flatMap(a => a.endpoints.map(e => e.protocol_type)))
        const protocolList = Array.from(protocols).map(p => p === 'http_unknown' ? 'HTTP' : p.toUpperCase()).join(', ')
        let summary = `Found ${totalCount} agents. ${callableCount} callable.`
        if (protocols.size > 0) summary += ` Available via ${protocolList}.`
        return summary
      }
      return content
    }

    const filterLabels = [...activeFilters].map(f => FILTER_CHIPS.find(c => c.key === f)!.label)
    const filtered = filteredAgents.length
    const total = displayedAgents.length
    if (filtered === 0) {
      return `No agents match filters: ${filterLabels.join(', ')}. Try removing a filter.`
    }
    const callableCount = filteredAgents.filter(a => a.availability === 'callable').length
    const protocols = new Set(filteredAgents.flatMap(a => a.endpoints.map(e => e.protocol_type)))
    const protocolList = Array.from(protocols).map(p => p === 'http_unknown' ? 'HTTP' : p.toUpperCase()).join(', ')
    let summary = `Showing ${filtered} of ${total} agents (${filterLabels.join(' + ')}). ${callableCount} callable.`
    if (protocols.size > 0) summary += ` Available via ${protocolList}.`
    return summary
  }, [content, activeFilters, displayedAgents, filteredAgents, agents, displayedTotal])

  const toggleFilter = async (key: FilterKey) => {
    const wasActive = activeFilters.has(key)
    setActiveFilters(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
    setCurrentPage(1)

    // Callable chip triggers a re-fetch to include/exclude non-callable agents
    if (key === 'callable' && isLatest && lastSearchParams) {
      setLoading(true)
      try {
        const { searchAgents } = await import('../../lib/api')
        const newParams = { ...lastSearchParams, offset: 0 }
        if (wasActive) {
          // Turning OFF callable → fetch all agents
          delete newParams.online_only
          delete (newParams as Record<string, unknown>).callable_only
        } else {
          // Turning ON callable → fetch callable only
          newParams.online_only = true
        }
        const result = await searchAgents(newParams)
        setSearchResultsStore(result.agents, result.total, lastQuery, newParams)
      } catch (err) {
        console.error('Failed to re-fetch:', err)
      } finally {
        setLoading(false)
      }
    }
  }

  const handleLoadMore = async () => {
    if (!lastSearchParams || isLoadingMore) return

    setLoadingMore(true)
    try {
      const { searchAgents } = await import('../../lib/api')
      const nextParams = {
        ...lastSearchParams,
        offset: (lastSearchParams.offset || 0) + lastSearchParams.limit,
      }
      const result = await searchAgents(nextParams)
      appendSearchResults(result.agents)
    } catch (err) {
      console.error('Failed to load more:', err)
    } finally {
      setLoadingMore(false)
    }
  }

  const goToPage = (page: number) => {
    setCurrentPage(page)
    // If on the last page of loaded results and there are more on the server, load them
    if (page === totalPages && hasMore) {
      handleLoadMore()
    }
  }

  return (
    <div className="space-y-4">
      {/* Text response */}
      <div className="prose prose-invert max-w-none">
        <p className="text-[var(--text-secondary)] font-mono text-sm leading-relaxed">
          {displayContent}
        </p>
      </div>

      {/* Filter chips */}
      {displayedAgents && displayedAgents.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {FILTER_CHIPS.map(({ key, label, emoji }) => {
            const active = activeFilters.has(key)
            return (
              <button
                key={key}
                onClick={() => toggleFilter(key)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-mono transition-all ${
                  active
                    ? 'bg-[var(--accent-teal)]/20 text-[var(--accent-teal)] border border-[var(--accent-teal)]/40'
                    : 'bg-[var(--bg-tertiary)] text-[var(--text-muted)] border border-[var(--border-default)] hover:border-[var(--border-subtle)] hover:text-[var(--text-secondary)]'
                }`}
              >
                <span>{emoji}</span>
                <span>{label}</span>
              </button>
            )
          })}
        </div>
      )}

      {/* Agent cards */}
      {paginatedAgents && paginatedAgents.length > 0 && (
        <div className="space-y-3">
          {paginatedAgents.map((agent, index) => (
            <motion.div
              key={agent.agent_key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(index * 0.05, 0.4), duration: 0.25 }}
            >
              <AgentCard agent={agent} />
            </motion.div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1 pt-2">
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-1.5 rounded-lg font-mono text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            &larr; Prev
          </button>
          {getPageNumbers(currentPage, totalPages).map((page, i) =>
            page === '...' ? (
              <span key={`ellipsis-${i}`} className="px-2 py-1.5 font-mono text-xs text-[var(--text-muted)]">...</span>
            ) : (
              <button
                key={page}
                onClick={() => goToPage(page as number)}
                className={`min-w-[32px] px-2 py-1.5 rounded-lg font-mono text-xs transition-all ${
                  currentPage === page
                    ? 'bg-[var(--accent-teal)] text-[var(--bg-primary)] font-bold'
                    : 'text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)]'
                }`}
              >
                {page}
              </button>
            )
          )}
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-1.5 rounded-lg font-mono text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            Next &rarr;
          </button>
          {hasMore && currentPage === totalPages && (
            <button
              onClick={handleLoadMore}
              disabled={isLoadingMore}
              className="ml-2 px-3 py-1.5 rounded-lg font-mono text-xs text-[var(--accent-teal)] hover:bg-[var(--accent-teal)]/10 border border-[var(--accent-teal)]/30 transition-all disabled:opacity-50"
            >
              {isLoadingMore ? 'Loading...' : 'Load more'}
            </button>
          )}
        </div>
      )}

      {/* No results after filtering */}
      {filteredAgents && filteredAgents.length === 0 && displayedAgents && displayedAgents.length > 0 && (
        <p className="text-[var(--text-muted)] font-mono text-sm py-4 text-center">
          No agents match the selected filters.
        </p>
      )}
    </div>
  )
}

/** Generate page numbers with ellipsis like Google: 1 ... 4 5 [6] 7 8 ... 12 */
function getPageNumbers(current: number, total: number): (number | '...')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)

  const pages: (number | '...')[] = []

  // Always show first page
  pages.push(1)

  if (current > 3) pages.push('...')

  // Pages around current
  const start = Math.max(2, current - 1)
  const end = Math.min(total - 1, current + 1)
  for (let i = start; i <= end; i++) pages.push(i)

  if (current < total - 2) pages.push('...')

  // Always show last page
  pages.push(total)

  return pages
}
